/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, TextField } from "@mui/material";
import debounce from "lodash.debounce";
import LabelledField from "components/labelled-field";
import { SVGIcons } from "cms/icons/svg-icons";
import { ImageInPageEditorV2 } from "cms/images/image-inpage-editor-v2";
import { cloneObjectsArray } from "utils/array";
import { AddImageButton } from "./add-image-button";
import { formatBytes, getIconForType } from "utils/format-bytes";

const DocumentDisplay = (props) => {
    
    const { t } = useTranslation();
    
    // Document selector props (from DocumentDisplayWrapper)
    const document = props?.document;
    const onChange = props?.onChange;
    const onRemove = props?.onRemove;
    
    // Card props for updating card data
    const card = props?.card;
    const index = props?.index;
    const cardsList = props?.cardsList;
    const externalCardsListRef = props?.cardsListRef;
    const updateCards = props?.updateCards;

    // Title: Auto-populate from document if card doesn't have one, but user can overwrite
    // Description: Never auto-populate - must be manual
    const [title, setTitle] = useState(card?.title || "");
    const [description, setDescription] = useState(card?.description || "");
    
    // Use refs to track if we're currently editing to prevent sync conflicts
    const isEditingTitleRef = useRef(false);
    const isEditingDescriptionRef = useRef(false);
    const lastCardTitleRef = useRef(card?.title);
    const lastCardDescriptionRef = useRef(card?.description);
    
    // Use refs to store latest cardsList and updateCards to prevent callback recreation
    const cardsListRef = useRef(cardsList);
    const updateCardsRef = useRef(updateCards);
    
    // Update refs when props change
    useEffect(() => {
        cardsListRef.current = cardsList;
        updateCardsRef.current = updateCards;
    }, [cardsList, updateCards]);

    const getCurrentCardsList = useCallback(() => {
        return externalCardsListRef?.current || cardsListRef.current;
    }, [externalCardsListRef]);

    // When document changes, populate title from document.title ONLY if card doesn't have a title
    // Title can be overwritten by user
    useEffect(() => {
        if (document && document?.guid) {
            // Only auto-populate title if card doesn't already have one
            if (!card?.title) {
                const documentTitle = document?.title || "";
                if (documentTitle) {
                    setTitle(documentTitle);
                    lastCardTitleRef.current = documentTitle;
                    // Update card with title from document (only if card doesn't have title)
                    if (cardsList && updateCards && index !== undefined) {
                        const updatedCards = cloneObjectsArray(cardsList);
                        updatedCards[index] = {
                            ...(card || {}),
                            title: documentTitle,
                        };
                        updateCards?.(updatedCards);
                    }
                }
            } else {
                // Card has a title (user has overwritten), use it
                if (!isEditingTitleRef.current) {
                    setTitle(card.title);
                    lastCardTitleRef.current = card.title;
                }
            }
        } else if (card?.title) {
            // No document but card has title, use card title
            if (!isEditingTitleRef.current) {
                setTitle(card.title);
                lastCardTitleRef.current = card.title;
            }
        } else {
            // No document and no card title, clear title
            if (!isEditingTitleRef.current) {
                setTitle("");
                lastCardTitleRef.current = "";
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [document?.title, document?.guid, card?.title]);

    // Description: Never auto-populate from document - must be populated manually
    // Only sync from card (which is manually set by user)
    useEffect(() => {
        if (!isEditingDescriptionRef.current && card?.description !== undefined && card.description !== description && card.description !== lastCardDescriptionRef.current) {
            setDescription(card?.description || "");
            lastCardDescriptionRef.current = card?.description;
        }
    }, [card?.description, description]);

    // Use refs to store latest title and description values for debounced handlers
    const titleRef = useRef(title);
    const descriptionRef = useRef(description);
    
    // Update refs when state changes
    useEffect(() => {
        titleRef.current = title;
    }, [title]);
    
    useEffect(() => {
        descriptionRef.current = description;
    }, [description]);

    // Debounced function to update card title
    const debouncedUpdateTitle = useRef(
        debounce((newTitle) => {
            isEditingTitleRef.current = false;
            const currentCardsList = getCurrentCardsList();
            const currentUpdateCards = updateCardsRef.current;
            
            if (currentCardsList && currentUpdateCards && index !== undefined) {
                const updatedCards = cloneObjectsArray(currentCardsList);
                // Get current card from cardsList to ensure we have the latest data
                const currentCard = currentCardsList[index] || {};
                // Include latest description from ref to prevent losing pending updates
                updatedCards[index] = {
                    ...currentCard,
                    title: newTitle,
                    description: descriptionRef.current ?? currentCard.description,
                };
                lastCardTitleRef.current = newTitle;
                currentUpdateCards?.(updatedCards);
            }
        }, 500)
    ).current;

    // Debounced function to update card description
    const debouncedUpdateDescription = useRef(
        debounce((newDescription) => {
            isEditingDescriptionRef.current = false;
            const currentCardsList = getCurrentCardsList();
            const currentUpdateCards = updateCardsRef.current;
            
            if (currentCardsList && currentUpdateCards && index !== undefined) {
                const updatedCards = cloneObjectsArray(currentCardsList);
                // Get current card from cardsList to ensure we have the latest data
                const currentCard = currentCardsList[index] || {};
                // Include latest title from ref to prevent losing pending updates
                updatedCards[index] = {
                    ...currentCard,
                    title: titleRef.current ?? currentCard.title,
                    description: newDescription,
                };
                lastCardDescriptionRef.current = newDescription;
                currentUpdateCards?.(updatedCards);
            }
        }, 500)
    ).current;

    const onChangeTitle = (event) => {
        const newTitle = event?.target?.value;
        isEditingTitleRef.current = true;
        setTitle(newTitle);
        titleRef.current = newTitle;
        // Debounce the card update
        debouncedUpdateTitle(newTitle);
    };

    const onChangeDescription = (event) => {
        const newDescription = event?.target?.value;
        isEditingDescriptionRef.current = true;
        setDescription(newDescription);
        descriptionRef.current = newDescription;
        // Debounce the card update
        debouncedUpdateDescription(newDescription);
    };

    // Get image GUID from banner_image object (banner_image.image contains the GUID)
    const getImageGuid = useCallback((bannerImageObj) => {
        if (!bannerImageObj) return null;
        // If it's a string (old format), return it
        if (typeof bannerImageObj === "string") return bannerImageObj;
        // If it's an object, extract the image GUID
        return bannerImageObj?.image || null;
    }, []);
    
    // Get settings from banner_image object
    const getSettings = useCallback((bannerImageObj) => {
        if (!bannerImageObj) return null;
        // If it's an object with settings, return settings
        if (typeof bannerImageObj === "object" && bannerImageObj?.settings) {
            return bannerImageObj.settings;
        }
        return null;
    }, []);
    
    // Get banner_image data for ImageInPageEditorV2 - memoize to prevent re-renders
    const bannerImageObj = useMemo(() => card?.banner_image, [card?.banner_image]);
    const imageGuid = useMemo(() => getImageGuid(bannerImageObj), [bannerImageObj, getImageGuid]);
    const settings = useMemo(() => getSettings(bannerImageObj), [bannerImageObj, getSettings]);
    
    // ImageInPageEditorV2 handlers - memoize to prevent re-renders
    // Use refs instead of direct dependencies to prevent recreation when cardsList changes
    const onChangeImageWithSettings = useCallback((imageObj, newSettings) => {
        const imageGuidValue = imageObj?.guid || "";
        
        // Update card with banner_image object structure
        const currentCardsList = getCurrentCardsList();
        const currentUpdateCards = updateCardsRef.current;
        
        if (currentCardsList && currentUpdateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            // Get current card from cardsList to ensure we have the latest data
            const currentCard = currentCardsList[index] || {};
            const currentBannerImage = currentCard?.banner_image;
            
            // Preserve existing settings if they exist, otherwise use defaults
            const settingsToUse = newSettings || currentBannerImage?.settings || {
                format: "image.format.cropped-focus",
                options: {
                    method: "100Aspect",
                    position: "center center",
                    dimension: 400,
                    useCaption: false
                }
            };
            
            updatedCards[index] = {
                ...currentCard,
                banner_image: {
                    image: imageGuidValue,
                    settings: settingsToUse
                },
            };
            currentUpdateCards?.(updatedCards);
        }
    }, [index, getCurrentCardsList]); // Only depend on index, not cardsList or updateCards

    const onRemoveImage = useCallback(() => {
        // Remove banner_image from card
        const currentCardsList = getCurrentCardsList();
        const currentUpdateCards = updateCardsRef.current;
        
        if (currentCardsList && currentUpdateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            // Get current card from cardsList to ensure we have the latest data
            const currentCard = currentCardsList[index] || {};
            updatedCards[index] = {
                ...currentCard,
                banner_image: null,
            };
            currentUpdateCards?.(updatedCards);
        }
    }, [index, getCurrentCardsList]); // Only depend on index, not cardsList or updateCards

    // Memoize ImageInPageEditorV2 props to prevent unnecessary re-renders
    const imageEditorProps = useMemo(() => ({
        image_guid: imageGuid,
        settings: settings,
        onChangeImageWithSettings: onChangeImageWithSettings,
        onRemoveImage: onRemoveImage,
        className: "document-display-image-inpage-editor-v2",
        AddButtonComponent: AddImageButton,
    }), [imageGuid, settings, onChangeImageWithSettings, onRemoveImage]);

    // Get document size formatted
    const documentSize = document?.file_size ? formatBytes(document.file_size) : "";

    return (
        <Fragment>
            {document && document?.guid && (
                <div className='document-editor'>
                    <div className='document-editor-view'>
                        <div className='document-display'>
                            <div className='document-actions'>
                                <div className="card-document-display-document-selector-content-icon">
                                    <SVGIcons.common.Page />
                                </div>
                                <div className="card-document-display-document-selector-content-info">
                                    <div className="card-document-display-document-selector-content-info-title">
                                        {document?.title}
                                    </div>
                                    <div className="card-document-display-document-selector-content-info-size">
                                        {documentSize}
                                    </div>
                                </div>
                                <div className='card-document-display-document-selector-content-actions'>
                                    <div className='image-action-change'>
                                        <IconButton onClick={onChange}>
                                            <SVGIcons.common.Edit />
                                        </IconButton>
                                    </div>
                                    <div className='image-action-remove'>
                                        <IconButton onClick={onRemove}>
                                            <SVGIcons.common.Close />
                                        </IconButton>
                                    </div>
                                </div>
                            </div>
                            
                            <LabelledField label={t?.("commons.title")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='document_title'
                                    fullWidth={true}
                                    value={title || ""}
                                    onChange={onChangeTitle}
                                />
                            </LabelledField>
                            <LabelledField label={t?.("commons.description")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='document_description'
                                    fullWidth={true}
                                    multiline={true}
                                    maxRows={4}
                                    value={description || ""}
                                    onChange={onChangeDescription}
                                />
                            </LabelledField>

                            {/* Image Editor */}
                            <LabelledField label="Image" style={{ marginTop: 12 }}>
                                <ImageInPageEditorV2 {...imageEditorProps} />
                            </LabelledField>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

DocumentDisplay.displayName = "DocumentDisplay";
export { DocumentDisplay };
